import md5 from 'js-md5'
import Encrypt from 'jsencrypt'
import { get } from 'lodash'

import { PUBLIC_KEY_1 } from './cache.js'
import { PUBLIC_KEY_2 } from './download.js'
import { PUBLIC_KEY_3 } from './exportUtils.js'

const SALT = process.env.VEHICLE_SALT
const PUBLIC_KEY = `${PUBLIC_KEY_1}${PUBLIC_KEY_2}${PUBLIC_KEY_3}`

const getEncryptSdk = async () => {
  return Encrypt
}

/**
 * 提取URL路径（最后一个/后的内容，不包含/）
 * @param {string} url - 完整URL
 * @returns {string} URL路径
 * @example
 * /v1/order/shopCreateOrder -> shopCreateOrder
 * /v1/business/shop/list -> list
 * /v1/user/channelLogin -> channelLogin
 */
const extractUrlPath = (url) => {
  if (!url) return ''

  // 去除域名和协议
  let path = url.replace(/^https?:\/\/[^/]+/, '')

  // 去除查询参数和hash
  path = path.split('?')[0].split('#')[0]

  // 提取最后一个斜杠后的内容
  const lastSlashIndex = path.lastIndexOf('/')
  if (lastSlashIndex !== -1) {
    return path.substring(lastSlashIndex + 1)
  }

  return path
}

/**
 * 从对象中根据路径提取值（支持深度路径）
 * @param {object} obj - 源对象
 * @param {string} path - 路径，如 'a.b.c'
 * @returns {any} 提取的值
 */
const getValueByPath = (obj, path) => {
  return get(obj, path)
}

/**
 * 判断是否为基本类型
 * @param {any} value - 待判断的值
 * @returns {boolean} 是否为基本类型
 */
const isPrimitiveType = (value) => {
  return value === null || typeof value !== 'object' || value instanceof Date || value instanceof RegExp
}


/**
 * 提取核心参数的值并拼接
 * @param {object} businessParam - 业务参数对象
 * @param {array} signParamPaths - 核心参数路径数组
 * @returns {string} 拼接后的值字符串
 * @throws {Error} 当数组元素为引用类型时抛出异常
 */
const extractSignParamValues = (businessParam, signParamPaths) => {
  if (!signParamPaths || !Array.isArray(signParamPaths)) {
    return ''
  }

  const values = []

  for (const path of signParamPaths) {
    const value = getValueByPath(businessParam, path)

    // 跳过 undefined 和 null
    if (value !== undefined && value !== null) {
      // 基本类型直接加签
      if (isPrimitiveType(value)) {
        values.push(String(value))
      } else if (Array.isArray(value)) {
        // 数组类型处理
        if (value.length === 0) {
          // 空数组不加签
          continue
        }

        const firstElement = value[0]
        // 判断第一个元素是否为引用类型
        if (!isPrimitiveType(firstElement)) {
          throw new Error(`数组元素为引用类型，无法加签。路径: ${path}, 元素类型: ${typeof firstElement}`)
        }
        // 只加签第一个元素
        values.push(String(firstElement))
      } else {
        // 非数组的引用类型（普通对象）抛出异常
        throw new Error(`引用类型无法加签。路径: ${path}, 类型: ${typeof value}`)
      }
    }
  }

  // 直接拼接，不添加分隔符
  return values.join('')
}


/**
 * RSA公钥加密
 */
export const encryptPublicData = async (publicKey, data) => {
  const JSEncrypt = await getEncryptSdk()
  const encrypt = new JSEncrypt()
  encrypt.setPublicKey(publicKey)
  return encrypt.encrypt(data)
}

/**
 * 新版签名生成（支持核心参数）
 * @param {string} url - 接口URL
 * @param {object} businessParam - 业务参数
 * @param {array} signParamPaths - 核心参数路径数组，如 ['spuId', 'user.mobile']
 * @returns {object} { ts, sign }
 */
export const generateSignWithParams = async (url, businessParam, signParamPaths) => {
  const ts = new Date().getTime()

  // 提取URL路径（最后一个/后的部分）
  const urlPath = extractUrlPath(url)

  // 提取核心参数值并拼接
  const signValues = extractSignParamValues(businessParam, signParamPaths)

  // 构建签名原文：{时间戳}{URL路径}{核心参数值}@{盐}
  const signatureText = `${ts}${urlPath}${signValues}@${SALT}`

  // MD5加密
  const secret = md5(signatureText)

  // RSA加密
  const sign = await encryptPublicData(PUBLIC_KEY, secret)

  return {
    ts,
    sign,
    signatureText,
  }
}
